
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import { Download, FileText, Loader2 } from 'lucide-react';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { generateInventoryReportPDF, generateMultipleFormats } from '../../../utils/pdfGenerator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import "jspdf-autotable";

interface InventoryProduct {
  id: string;
  code: string | null;
  name: string;
  description?: string | null;
  unit_price: number | null;
  minimum_threshold: number | null;
  dosage: string;
  packaging: string;
  professional_id: string;
  period_id: string;
  category_id: string | null;
  category: {
    name: string;
    description: string | null;
  } | null;
  inventory_movements: {
    current_stock: number;
    movement_date: string;
  }[];
}

interface InventoryPeriod {
  id: string;
  period_name: string;
  start_date: string;
  end_date: string;
}

const COLORS = ['#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

export const InventoryReport = ({
  periodId
}: {
  periodId: string;
}) => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const {
    data: products,
    isLoading: isLoadingProducts,
    refetch: refetchProducts
  } = useQuery({
    queryKey: ['inventory-products-with-stock', periodId],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('inventory_products').select(`
          *,
          category:product_categories(
            name,
            description
          ),
          inventory_movements(
            current_stock,
            movement_date
          )
        `).eq('period_id', periodId).order('name');
      if (error) {
        console.error('Error fetching inventory:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les données d'inventaire"
        });
        throw error;
      }
      return data as InventoryProduct[];
    }
  });
  
  const {
    data: period,
    isLoading: isLoadingPeriod
  } = useQuery({
    queryKey: ['inventory-period', periodId],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('inventory_periods').select('*').eq('id', periodId).single();
      if (error) {
        console.error('Error fetching period:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les données de la période"
        });
        throw error;
      }
      return data as InventoryPeriod;
    }
  });

  // Set up real-time subscription
  React.useEffect(() => {
    const channel = supabase.channel('inventory-report-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'inventory_movements',
        filter: `period_id=eq.${periodId}`
      }, () => {
        console.log('Inventory movement detected, refetching report...');
        refetchProducts();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'inventory_products',
        filter: `period_id=eq.${periodId}`
      }, () => {
        console.log('Product update detected, refetching report...');
        refetchProducts();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [periodId, refetchProducts]);

  const handleGeneratePDF = async () => {
    if (!products || !period) {
      toast({
        title: "Erreur",
        description: "Données insuffisantes pour générer le rapport",
        variant: "destructive"
      });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Prepare data for PDF generation
      const reportData = {
        periodName: period.period_name,
        startDate: period.start_date,
        endDate: period.end_date,
        products: products.map(product => {
          const currentStock = product.inventory_movements?.[0]?.current_stock || 0;
          const unitPrice = product.unit_price || 0;
          return {
            code: product.code,
            name: product.name,
            dosage: product.dosage,
            currentStock,
            unitPrice,
            totalValue: unitPrice * currentStock,
            minimumThreshold: product.minimum_threshold
          };
        })
      };
      
      await generateInventoryReportPDF(reportData);
      toast({
        title: "Succès",
        description: "Le rapport PDF a été généré avec succès",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de générer le rapport PDF"
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleGenerateFormat = async (format: string) => {
    if (!products || !period) {
      toast({
        title: "Erreur",
        description: "Données insuffisantes pour générer le rapport",
        variant: "destructive"
      });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Prepare data for report generation
      const reportData = {
        periodName: period.period_name,
        startDate: period.start_date,
        endDate: period.end_date,
        products: products.map(product => {
          const currentStock = product.inventory_movements?.[0]?.current_stock || 0;
          const unitPrice = product.unit_price || 0;
          return {
            code: product.code,
            name: product.name,
            dosage: product.dosage,
            currentStock,
            unitPrice,
            totalValue: unitPrice * currentStock,
            minimumThreshold: product.minimum_threshold
          };
        })
      };
      
      if (format === 'pdf') {
        await generateInventoryReportPDF(reportData);
        toast({
          title: "Succès",
          description: "Le rapport PDF a été généré avec succès",
        });
      } else if (format === 'multi') {
        await generateMultipleFormats(reportData, 'inventory');
        toast({
          title: "Succès",
          description: "Les rapports ont été générés avec succès",
        });
      }
    } catch (error) {
      console.error(`Error generating ${format}:`, error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: `Impossible de générer le rapport au format ${format}`
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoadingProducts || isLoadingPeriod) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Chargement des données...</span>
      </div>
    );
  }

  if (!products) {
    return <div>Aucune donnée disponible</div>;
  }

  // Group products by category and prepare data for pie chart
  const productsByCategory = products.reduce((acc, product) => {
    const categoryName = product.category?.name || 'Non catégorisé';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(product);
    return acc;
  }, {} as Record<string, InventoryProduct[]>);
  
  const pieChartData = Object.entries(productsByCategory).map(([category, prods], index) => ({
    name: category,
    value: prods.reduce((sum, p) => {
      const currentStock = p.inventory_movements?.[0]?.current_stock || 0;
      const unitPrice = p.unit_price || 0;
      return sum + (currentStock * unitPrice);
    }, 0),
    color: COLORS[index % COLORS.length]
  }));
  
  // Calculate total value for summary
  const totalValue = products.reduce((sum, product) => {
    const currentStock = product.inventory_movements?.[0]?.current_stock || 0;
    const unitPrice = product.unit_price || 0;
    return sum + (currentStock * unitPrice);
  }, 0);
  
  const lowStockProducts = products.filter(product => 
    product.minimum_threshold && 
    (product.inventory_movements?.[0]?.current_stock || 0) <= product.minimum_threshold
  );
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Rapport d'inventaire</h3>
          {period && (
            <p className="text-sm text-muted-foreground">
              Période: {period.period_name} ({new Date(period.start_date).toLocaleDateString()} - {new Date(period.end_date).toLocaleDateString()})
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={isGenerating} className="gap-2">
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>Exporter</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleGenerateFormat('pdf')}>
              <FileText className="h-4 w-4 mr-2" /> PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleGenerateFormat('multi')}>
              <FileText className="h-4 w-4 mr-2" /> Tous les formats
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Produits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Valeur Totale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalValue.toFixed(2)} €</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Stock Faible</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{lowStockProducts.length}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Distribution par catégorie</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => [`${Number(value).toFixed(2)} €`, 'Valeur']}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Product Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Référence</TableHead>
              <TableHead>Nom</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead className="text-right">Prix unitaire</TableHead>
              <TableHead className="text-right">Valeur</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map(product => {
              const currentStock = product.inventory_movements?.[0]?.current_stock || 0;
              const unitPrice = product.unit_price || 0;
              const isLowStock = product.minimum_threshold && currentStock <= product.minimum_threshold;
              
              return (
                <TableRow 
                  key={product.id}
                  className={isLowStock ? "bg-red-50 dark:bg-red-900/10" : ""}
                >
                  <TableCell>{product.code || '-'}</TableCell>
                  <TableCell>
                    <div className="font-medium">{product.name}</div>
                    <div className="text-xs text-muted-foreground">{product.dosage}</div>
                  </TableCell>
                  <TableCell>{product.category?.name || 'Non catégorisé'}</TableCell>
                  <TableCell className="text-right">
                    <span className={isLowStock ? "text-red-600 font-medium" : ""}>
                      {currentStock}
                    </span>
                    {isLowStock && (
                      <div className="text-xs text-red-600">
                        Seuil: {product.minimum_threshold}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {unitPrice ? `${unitPrice.toFixed(2)} €` : '0.00 €'}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {(unitPrice * currentStock).toFixed(2)} €
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      
      {/* Low Stock Warning Section */}
      {lowStockProducts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Produits en stock faible</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {lowStockProducts.map(product => (
                <li key={product.id} className="text-sm">
                  <span className="font-medium">{product.name}</span> - Stock actuel: 
                  <span className="font-bold text-red-600 ml-1">
                    {product.inventory_movements?.[0]?.current_stock || 0}
                  </span> 
                  (Seuil min.: {product.minimum_threshold})
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
